<template>
  <main role="main" class="py-4">
    <b-container fluid="xxl">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("outputStatistic") }}</h4>
        <div>
          <b-button v-if="!isExportingToExcel" variant="outline-success" :disabled="!totalItems || isExportingToExcel"
            @click="debouncenOnClickExportToExcel">
            <b-icon-download class="mr-2"></b-icon-download> {{ $t("export") }}
          </b-button>
          <b-button v-if="isExportingToExcel" variant="outline-success">
            <span class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>{{
              $t("exporting") }} ...
          </b-button>
        </div>
      </div>
      <b-form class="d-flex flex-row align-items-center mb-3" @submit.prevent="debounceOnClickSearchBtn">
        <b-form-input class="mr-3" type="search" style="width: 280px;" minlength="2" maxlength="200"
          v-model="searchText" :placeholder="$t('search')" />
        <div class="flex-shrink-0 mr-3">{{ $t("saleArea") }}</div>
        <b-form-select v-if="isUserAdmin()" class="mr-3" :options="searchAreaOptions" v-model="searchAreaId"
          style="width: 280px;"></b-form-select>
        <div class="flex-grow-1 d-flex flex-row align-items-center mr-3">
          <div class="flex-shrink-0 mr-3">{{ $t("meetingDate") }}</div>
          <date-picker v-model="dateRange" range format="DD/MM/YYYY" value-type="date" :not-after="new Date()"
            :lang="lang" input-class="form-control" style="width: 100%;"
            :placeholder="`${$t('fromDate')} - ${$t('toDate')}`" range-separator=" - " @change="handleDateChange" />
        </div>
        <div style="margin-left: 1em; align-items: center; justify-content: flex-end;"
          class="d-flex flex-row  flex-grow-1">
          <b-button @click="showFilter" variant="primary" style="margin-right: 1px;
             padding: 6px; margin-left: 5px;" :title="$t('')" :disabled="!!(decreeChoose)">
            <b-icon-funnel /></b-button>
          <b-button type="submit" size="md" class="flex-shrink-0 ml-1" variant="primary">{{
            $t("search") }}</b-button>
        </div>
      </b-form>
      <div>
        <div class="text-danger">{{ formErrorMsg }}</div>
      </div>
      <div class="table-container position-relative">
        <b-table ref="table" :items="tableItemProvider" hover primary-key="id" :fields="tableFields" :busy="isLoading"
          show-empty :per-page="itemsPerPage" :current-page="currentPage" responsive>
          <template v-slot:cell(id)="data">
            <router-link :to="`/customers/details/${data.item.id}/meetings`"
              class="text-primary small text-decoration-none" target="_blank">
              {{ data.item.id }}
            </router-link>
          </template>
          <template v-slot:cell(customerInfo)="data">
            <div class="small text-break">{{ data.item.name }}</div>
            <div class="small">{{ data.item.phoneNumber }}</div>
            <div class="small">{{ data.item.email }}</div>
          </template>
          <template v-slot:cell(saleAreaName)="data">
            <span class="small text-wrap">{{ data.item.saleAreaName }}</span>
          </template>
          <template v-slot:cell(unitName)="data">
            <span class="small text-wrap">{{ data.item.unitName }}</span>
          </template>
          <template v-slot:cell(subscriptionPlan)="data">
            <div>
              <div class="small">
                <span class=""> {{ data.item.subPlanName || $t('noData') }}</span>
              </div>
              <div class="small" v-if="data.item.meetingConcurrentParticipantsLimit">
                <span> {{ data.item.meetingConcurrentParticipantsLimit }} {{ $t("person") }} / {{ $t("CH") }}</span>
              </div>
              <div class="small" v-if="data.item.meetingCountDailyLimit">
                <span> {{ data.item.meetingCountDailyLimit }} {{ $t("concurrent") }}</span>
              </div>
              <div class="small" v-if="data.item.maxStorageSize">
                <span> {{ $t("storage") }}: {{ convertToGB(data.item.maxStorageSize) }}</span>
              </div>
            </div>
          </template>
          <template v-slot:cell(cusSubEndDate)="data">
            <div class="small" v-if="data.item.cusSubEndDate">
              {{ convertDateOverView(data.item.cusSubEndDate) }}
            </div>
            <div class="small" v-if="data.item.remainingDays">
              <span v-if="data.item.remainingDays > 0"
                :class="{ 'text-warning': (data.item.remainingDays < 7 && data.item.remainingDays > 0) }">{{
                  $t("remainDays", {
                    date:
                      data.item.remainingDays
                  }) }}</span>
              <span class="text-danger" v-if="data.item.remainingDays <= 0">
                {{ $t("expiredPackage") }}
              </span>
            </div>
          </template>
          <template v-slot:cell(meetingStats)="data">
            <div>
              <div class="small">
                <span class="">{{ $t("sum") }}:</span> {{ data.item.totalMeetings }}
              </div>
              <div class="small">
                <span class="">{{ $t("timeMeeting") }}:</span> {{ convertToHHMMSS(data.item.totalActualDuration) }}
              </div>
            </div>
          </template>
          <template v-slot:cell(recordingStats)="data">
            <div>
              <div class="small">
                <span class="">{{ $t("sum") }}:</span> {{ data.item.totalMeetingsRecorded }}
              </div>
              <div class="small">
                <span class="">{{ $t("storageMeeting") }}:</span> {{ convertToGB(data.item.totalRecordingSize) }} 
              </div>
            </div>
          </template>
          <template v-slot:cell(createdTime)="data">
            <span class="small">{{ convertDateOverView(data.item.createdTime) }}</span>
            <div class="small">{{ localTimeFormat(data.item.createdTime) }}</div>
          </template>
          <template v-slot:cell(totalRecordingSize)="data">
            
            <div v-if="data.item.totalMeetingsSummary > 0">
              <span class="small">{{$t("sum")}}: {{ data.item.totalMeetingsSummary }}</span>
              <br/>
              <span class="small">{{ data.item.totalSummaryDuration ? "T/lg"+ ": " + convertToHHMMSS(data.item.totalSummaryDuration) : "" }}</span>            
            </div>
          </template>
          <template v-slot:cell(actions)="data">
            <b-dropdown size="sm" variant="link" toggle-class="p-0 text-dark" right no-caret
              menu-class="shadow-sm rounded">
              <template #button-content>
                <b-icon icon="three-dots-vertical" />
              </template>
              <b-dropdown-item @click="showDetail(data.item)">
                {{ $t("viewDetails") }}
              </b-dropdown-item>
            </b-dropdown>
          </template>
        </b-table>
        <div v-if="isLoading" class="loader-overlay">
          <div class="spinner"></div>
          <div>{{ $t("loadingData") }}</div>
        </div>
      </div>
      <div class="d-flex flex-row align-items-center justify-content-between">
        <div></div>
        <div class="d-flex flex-row align-items-center">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}
          </div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems"
            :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>
      <!--Modal advance filter-->
      <b-modal id="bv-modal-filter" :title="$t('advancefilter')" class="filter-advanced" hide-footer
        style="padding-top:8px">
        <b-form class="d-flex flex-row align-items-center mb-3" @submit.prevent="debounceOnClickAdvanceSearchBtn">
          <div id="filterAdvance">
            <div class="d-flex flex-column">
              <!--Khu vực kinh doanh-->
              <div class="mb-2">
                <div class="mb-2">{{ $t("saleArea2") }}:</div>
                <div>
                  <b-form-select size="md" :options="searchAreaOptions" v-model="searchAreaId" />
                </div>
              </div>
              <!--Khu vực-->
              <div class="mb-2">
                <div class="mb-2">{{ $t("saleArea") }}:</div>
                <div>
                  <b-form-select size="md" :options="areaOptions" v-model="areaOption" />
                </div>
              </div>
              <!-- Cây Đơn vị, phòng ban-->
              <div class="mb-2">
                <div class="mb-2">{{ $t("unitDepart") }}:</div>
                <div>
                  <VueTreeselect v-model="unitOption" :options="unitOptions" :normalizer="normalizer"
                    :default-expand-level="1" :clearable="true" :searchable="true" placeholder="Chọn đơn vị VNPT" />
                </div>
              </div>
              <div class="mb-2">
                <div class="mb-2">{{ $t("accountCreatedDate") }} :</div>
                <div>
                  <date-picker v-model="dateRangeCreateAccount" range format="DD/MM/YYYY" value-type="date"
                    :not-after="new Date()" :lang="lang" input-class="form-control" style="width: 100%;"
                    :placeholder="`${$t('fromDate')} - ${$t('toDate')}`" range-separator=" - "
                    @change="handleDateChangeCreateAccount" />
                </div>
              </div>
              <div class="mb-2">
                <div class="mb-2">{{ $t("meetingDate") }} :</div>
                <div>
                  <date-picker v-model="dateRangeMeetings" range format="DD/MM/YYYY" value-type="date"
                    :not-after="new Date()" :lang="lang" input-class="form-control" style="width: 100%;"
                    :placeholder="`${$t('fromDate')} - ${$t('toDate')}`" range-separator=" - "
                    @change="handleDateChangeMeetings" />
                </div>
              </div>
              <div class="mb-2">
                <div class="mb-2">{{ $t("subscriptionPlan") }}:</div>
                <div>
                  <b-form-select size="md" :options="subscriptionPlans" v-model="subscriptionPlanId" />
                </div>
              </div>
              <div class="mb-2">
                <div class="mb-2">{{ $t("desc") }}:</div>
                <div>
                  <b-form-select size="md" :options="selectOptions" v-model="selectedOption" />
                </div>
              </div>
              <div class="d-flex flex-row" style="justify-content: space-between; margin-top: 10px">
                <b-button @click="clearFilter()" style="width: 49%" size="md" class="flex-shrink-0"
                  variant="outline-secondary">
                  {{ $t("clearFilter") }}
                </b-button>
                <b-button type="submit" style="width: 49%" size="md" class="flex-shrink-0" variant="primary">
                  {{ $t("apply") }}
                </b-button>
              </div>
            </div>
          </div>
        </b-form>
      </b-modal>
      <!--end modal advance filter-->
      <!--Modal detail-->
      <template>
        <div>
          <b-modal id="bv-modal-detail" @shown="onModalShown"   no-close-on-backdrop  no-close-on-esc scrollable hide-footer>
            <!-- Custom Header -->
            <template #modal-header>
              <div class="custom-modal-header w-100">
                <div class="d-flex justify-content-between align-items-center">
                  <!-- Left Section -->
                  <div class="d-flex align-items-center">
                    <h5 v-if="!isExportingToPDF" class="modal-title mb-0 ml-3 pl-3"> {{customerDetail.id }} - {{
                     customerDetail.name }}</h5>
                    <h5 v-if="isExportingToPDF" class="modal-title mb-0 ml-3 pl-3 text-primary"> <span
                        class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>{{
                          $t("exporting") }} ...</h5>
                  </div>
                  <!-- Right Section -->
                  <div class="header-actions no-export" style="width: 60%; position: absolute; top: 10px; right: 0px;text-align: right;">
                    <template>
                      <date-picker class="no-export" v-model="dateRangeDetailFilter" range format="DD/MM/YYYY"
                        value-type="date" :not-after="new Date()" :lang="lang" input-class="form-control"
                        style="width:40%;margin-top:3px;height: 37px!important;" :placeholder="`${$t('fromDate')} - ${$t('toDate')}`" range-separator=" - "
                        @change="onDateRangeDetailChange" />
                    </template>
                    <!-- Button Export PDF với loading -->
                    <b-button variant="primary" size="sm" class="mr-2 ml-2" style="margin-top:-4px;padding-top:7px;padding-bottom: 7px;height: 38px;"
                      @click="debouncenExportFullPageToPDF" :disabled="isExportingToPDF">
                      <i class="fas fa-print mr-2"></i>
                      {{ $t("exportPDF") }}
                    </b-button>
                    <b-button style="margin-top:-4px;border:0px;color:#fff" variant="link" class="custom-close-button mr-2 ml-2  text-decoration-none btn btn-danger"  size="sm"
                      @click="hideModal()">
                      <i class="fas fa-times" style="padding:7px"></i>
                    </b-button>
                  </div>
                </div>
              </div>
            </template>
            <!-- Modal Content -->
            <div class="modal-body export-area">
              <!--Section 1-->
              <b-row v-if="!isLoadingOverviewData && overviewData" class="my-0 pt-0 p-2 pb-0">
                <b-col v-if="isCustomer()" cols="6" lg="3" class="p-2">
                  <div class="text-center rounded py-3 h-100 d-flex flex-column justify-content-center"
                    :style="{ 'background-color': '#D7F0F7' }">
                    <div><label class="mb-0">{{ $t('subPlanName') }}</label></div>
                    <div><strong style="font-size: 1.25rem">{{ activeSubscriptionPlan.name }}</strong></div>
                  </div>
                </b-col>
                <b-col v-if="isCustomer()" cols="6" lg="3" class="p-2">
                  <div class="text-center rounded py-3 h-100 d-flex flex-column justify-content-center"
                    :style="{ 'background-color': '#D7F0F7' }">
                    <div><label class="mb-0">{{ $t('expDate') }}</label></div>
                    <div><strong style="font-size: 1.25rem">{{ convertDateOverView(activeCustomerSubscription.endTime)
                        }}</strong>
                    </div>
                  </div>
                </b-col>
                <b-col v-for="overviewItem in overviewItems.filter(item => item.key !== 'subscriptionPlanId')"
                  :key="overviewItem.key" cols="6" lg="3" class="p-2">
                  <div v-if="overviewItem.key === 'subPlanName'" v-b-tooltip.hover.html :title="featureString"
                    @mouseover="debouncehandleHover()"
                    class="text-center rounded py-3 h-100 d-flex flex-column justify-content-center"
                    :style="{ 'background-color': '#D7F0F7' }">
                    <a style="cursor: pointer;">
                      <div><label class="mb-0">{{ overviewItem.text }}</label></div>
                      <div><strong style="font-size: 1.25rem">{{ overviewItem.value }}</strong></div>
                    </a>
                  </div>
                  <div v-else class="text-center rounded py-3 h-100 d-flex flex-column justify-content-center"
                    :style="{ 'background-color': '#D7F0F7' }">
                    <a style="cursor: pointer;">
                      <div><label class="mb-0">{{ overviewItem.text }}</label></div>
                      <div><strong style="font-size: 1.25rem">{{ overviewItem.value }}</strong></div>
                    </a>
                  </div>
                </b-col>
              </b-row>
              <!--Section 2-->
              <div v-if="customer">
                <div class="row">
                  <h5 v-if="customer" class="p-1 mb-0 mb-3" style="width: 97%;">{{ $t("info") }}</h5>
                  <i class="no-export" @click="showInfo = !showInfo"
                    :class="showInfo ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                    style="margin-top: 7px;cursor: pointer;position:absolute;right:20px;"></i>
                </div>
                <transition name="slide-fade">
                  <div v-if="showInfo">
                    <div class="row customer-statistic p-2 mx-0">
                      <template v-if="customer">
                        <b-row style="width: 100%;" class="mx-0">
                          <b-col lg="6" class="border-right p-3 pl-5">
                            <b-row class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("customerId") }}:</span></b-col>
                              <b-col cols="8">{{ customer.customerCode }}</b-col>
                            </b-row>
                            <b-row class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("customerName") }}:</span></b-col>
                              <b-col cols="8" class="text-break">{{ customer.name }}</b-col>
                            </b-row>
                            <b-row v-if="customer.corporations" class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("underTheUnit") }}:</span></b-col>
                              <b-col cols="8">{{ customer.corporations }}</b-col>
                            </b-row>
                            <b-row v-else class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("ownedBy") }}:</span></b-col>
                              <b-col cols="8"></b-col>
                            </b-row>
                            <b-row class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("email") }}:</span></b-col>
                              <b-col cols="8">{{ customer.email }}</b-col>
                            </b-row>
                            <b-row class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("phoneNumber") }}:</span></b-col>
                              <b-col cols="8">{{ customer.phoneNumber }}</b-col>
                            </b-row>
                            <b-row class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("address") }}:</span></b-col>
                              <b-col cols="8" class="text-break">{{ customer.address }}</b-col>
                            </b-row>
                            <b-row>
                              <b-col cols="4"><span class="">{{ $t("status") }}:</span></b-col>
                            </b-row>
                          </b-col>
                          <b-col lg="6" class="p-3 pl-5">
                            <b-row class="align-items-center">
                              <b-col cols="4">
                                <span class="">{{ $t("subscriptionPlan") }}:</span>
                              </b-col>
                              <b-col cols="8" v-if="activeSubscription">
                                {{ activeSubscription.subscriptionPlanName }} -
                                <span v-if="activeSubscription.remainingDays > 0">
                                 {{ $t("remainDays",{date: activeSubscription.remainingDays }) }}
                                </span>
                                <span class="text-danger" v-if="activeSubscription.remainingDays < 0">
                                  {{ $t("expiredPackage") }}
                                </span>
                              </b-col>
                              <b-col cols="8" v-else>
                                {{ $t("cancelled") }}
                              </b-col>
                            </b-row>
                            <b-row class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("saleArea") }}:</span></b-col>
                              <b-col cols="8">{{ customer.areaName }} (id: {{ customer.areaId }})</b-col>
                            </b-row>
                            <b-row v-if="customer.taxCode" class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("taxCode") }}:</span></b-col>
                              <b-col cols="8">{{ customer.taxCode }}</b-col>
                            </b-row>
                            <b-row class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("documentType") }}:</span></b-col>
                              <b-col cols="8">{{ customer.documentTypeName }}</b-col>
                            </b-row>
                            <b-row class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("documentNumber") }}:</span></b-col>
                              <b-col cols="8">{{ customer.idCard }}</b-col>
                            </b-row>
                            <b-row class="align-items-center">
                              <b-col cols="4"><span class="">{{ $t("storageCapacity") }}:</span></b-col>
                              <b-col cols="8">{{ customer.recordingStorage }} MB</b-col>
                            </b-row>
                            <b-row class="align-items-center">
                          
                              <b-col cols="4"><span class="">{{ $t("note") }}:</span></b-col>
                              <b-col cols="8" class="text-break">{{ customer.summary }}</b-col>
                            </b-row>
                          </b-col>
                        </b-row>
                      </template>
                    </div>
                  </div>
                </transition>
              </div>
              <!--Section 3-->
              <div class="row">
                <h5 v-if="customer" class="p-1 mb-0 mb-3 mt-3" style="width: 97%;">{{ $t("meetingStatis") }}</h5>
                <i class="no-export" @click="showChart = !showChart"
                  :class="showChart ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                  style="margin-top: 27px; cursor: pointer;position:absolute;right:20px;"></i>
              </div>
              <transition name="slide-fade">
                <div v-if="showChart"
                  class="row customer-statistic p-2 mx-0 d-flex justify-content-center align-items-center"
                  style="height: 500px;margin-bottom: 10px;">
                  <template>
                    <div class="text-secondary" v-if="!isShowChart" style="margin:auto;">{{ chartFilterFormErrorMsg }}
                    </div>
                    <v-chart v-show="isShowChart" ref="chartRef" class="chart"
                      :init-options="{ height: 'auto', width: 'auto' }" :option="chartDetailOptions"
                      style="height: 500px; width: 100%;" autoresize />
                    <span v-show="!isShowChart" class="text-secondary no-data" style="margin:auto">{{ $t("noData")
                    }}</span>
                  </template>

                </div>
              </transition>
              <!--Section 4-->
              <div class="row mt-3">
                <div class="p-1 ps-3 w-100">
                  <div class="d-flex flex-row flex-nowrap align-items-center overflow-auto w-100">
                    <div class="d-flex align-items-center flex-nowrap w-100">
                      <h5 class="mb-0 me-auto" style="width: 80%;">{{ $t("meetingList") }}</h5>
                      <div class="d-flex align-items-center flex-shrink-0  no-export mr-2">
                        <template>
                          <b-form-select v-model="filterStatus" :options="statusOptions"
                            class="me-3 flex-shrink-0 no-export custom-select"
                            style="width: 180px; padding: 0px 0px 0px 1rem !important;" @change="onStatusSelect" />
                        </template>
                        <button @click="debounceSearchFromDetail()" type="button" size="lg"
                          class="btn btn-primary btn-sm flex-shrink-0 no-export me-3 ml-1" style="padding:8px">{{
                            $t("search") }}</button>
                        <b-button variant="success" size="lg"
                          class="btn btn-success btn-sm flex-shrink-0 no-export ml-1"
                          @click="debounceExportExcellMeetingsList" style="padding:8px;" :disabled="isExportingToExcel">
                          <span v-if="isExportingToExcel" class="spinner-border spinner-border-sm me-1" role="status"
                            aria-hidden="true"></span>
                          {{ isExportingToExcel ? $t("exporting") : $t("export") }}
                        </b-button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="row p-2 mx-0 no-export">
                <div class="table-container position-relative" style="width: 100%;">
                  <b-table ref="table_Meetings" :busy="isLoading" :items="tableItemProvider_Meetings" hover show-empty primary-key="id"
                    :fields="tableFields_Meetings" :per-page="itemsPerPage_Meetings"
                    :current-page="currentPage_Meetings">
                    <template v-slot:table-busy>
                      <div class="text-center py-4">{{ $t("loadingData") }}</div>
                    </template>
                    <template v-slot:empty>
                      <div class="text-center text-secondary no-data">{{ $t("noData") }}</div>
                    </template>
                    <template v-slot:cell(id)="data">
                       <router-link  
                          v-if="(data.item.owner || isUserAdmin()) && !data.item.deletedByName"
                          :to="`/meetings/details/${data.item.id}`" >
                          {{ data.item.id }}
                        </router-link>

                        <span v-else class="text-muted"> {{ data.item.id }} </span>
                    </template>
                    <template v-slot:cell(name)="data">
                      <div style="max-width: 60vh; white-space: normal; word-break: break-word;">{{ data.item.name }}
                      </div>
                    </template>
                    <template v-slot:cell(startTime)="data">
                      <div>{{ (data.item.startTime) }}</div>
                      <div v-if="data.item.meetingTime">{{ $t("TG") }} : {{ (data.item.meetingTime) }}</div>
                      <div v-if="data.item.duration">{{ $t("TL") }}: {{convertToHHMMSS(data.item.duration) }}</div>
                      <div v-if="data.item.actualParticipantCount">{{ $t("TGia") }} : {{ (data.item.actualParticipantCount) }}</div>
                    </template>
                    <template v-slot:cell(recordingSize)="data">
                      <div style="white-space: pre-wrap;">
                      {{ getLabelForRecord(data.item) }}         
                      </div>     
                    </template>
                    <template v-slot:cell(usedSummary)="data">
                         <div style="white-space: pre-wrap;">
                         {{ getLabelForSummary(data.item) }}            
                         </div>  
                    </template>
                    <template v-slot:cell(subscriptionPlanName)="data">
                      <div v-if="data.item.subscriptionPlanName">{{ data.item.subscriptionPlanName }}</div>
                      <div v-if="data.item.meetingConcurrentParticipantsLimit">{{
                        data.item.meetingConcurrentParticipantsLimit }} {{ $t("perMeeting") }}</div>
                      <div v-if="data.item.meetingCountDailyLimit">{{ data.item.meetingCountDailyLimit }} {{ $t("meetDay") }}</div>
                      <div v-if="data.item.maxStorageSize">{{ $t("storage") }}: {{ convertToGB(data.item.maxStorageSize) }}</div>
                    </template>
                    <template v-slot:cell(createdAt)="data">
                      {{ (data.item.createdAt) }}
                    </template>
                    <template v-slot:cell(deletedByName)="data">
                      {{ data.item.deletedByName }}
                    </template>
                    <template v-slot:cell(meetingStatus)="data">
                       <div v-if="data.item.deletedByName">
                        <b-badge variant="danger">{{ $t("deleted") }}</b-badge>
                        <br/>
                        {{ (data.item.deletedByName) }}
                      </div>
                      <div v-else>
                        <b-badge :variant="statusBadgeVariantEnum[data.item.meetingStatus]">{{
                          getStatusTranslate(statusTextEnum[data.item.meetingStatus]) }}</b-badge>
                      </div>
                    </template>
                  </b-table>
                  <div v-if="isLoadingMeetings"
                    class="loader-overlay d-flex flex-column justify-content-center align-items-center">
                    <div class="spinner"></div>
                    <div>{{ $t("loadingData") }}</div>
                  </div>
                </div>
              </div>
            </div>
            <div class="d-flex flex-row align-items-center justify-content-between">
              <div></div>
              <div class="d-flex flex-row align-items-center">
                <div v-if="totalItems_Meetings" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems_Meetings })
                }}
                </div>
                <b-pagination class="my-0 mr-3" v-model="currentPage_Meetings" :total-rows="totalItems_Meetings"
                  :per-page="itemsPerPage_Meetings"></b-pagination>
                <b-form-select v-model="itemsPerPage_Meetings" :options="itemsPerPageSelections"></b-form-select>
              </div>
            </div>
          </b-modal>
        </div>
      </template>
      <!--end modal detail-->
    </b-container>
  </main>
</template>
<style>
.custom-close-button
{

    color: #fff!important;
    border:0px inset #dc3545!important;
    width: 38px;height: 38px;
    padding-left:7px!important;

}
.custom-close-button:hover
{
    color: #fff!important;
}
.no-scroll {
  overflow: hidden !important;
}

.chart {
  width: 100%;
}

.custom-select {
  font-family: "Source Sans Pro" !important;
}

td,
td.text-break {
  vertical-align: top !important;
}

.no-data {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 16px;
  border: 1px dashed #ccc;
  border-radius: 8px;
  background: #fafafa;
}

table td,
table th {
  border: 0px solid #dadada !important;
}

#modal-header,
#bv-modal-detail .modal-header {
  margin: 0px !important;
  border: 0px !important;
  padding: 0px !important
}

.customer-card {
  border: 0px solid #dee2e6;
  border-radius: 0px;
  padding: 20px;
}

.border-left {
  border-left: 1px solid #dadada;
  ;
}

.field-row {
  display: flex;
  margin-bottom: 15px;
  align-items: center;
}

.field-label {
  font-weight: bold;
  color: #495057;
  width: 70%;
  padding-right: 15px;
}

.field-value {
  width: auto;
  padding: 8px 12px;
  border-radius: 4px;
  text-align: left;
}

.margin-left-right-1px {
  margin-left: 1px !important;
  margin-right: 1px !important;
}

.header {
  background-color: #0d6efd;
  color: white;
  padding: 15px;
  border-radius: 8px 8px 0 0;
  text-align: center;
  margin-bottom: 20px;
}

.ml-12px {
  margin-left: 12px !important;
}

.modal-body {
  padding-left: 20px !important;
  padding-right: 20px !important;
}

.card-statis {
  border-radius: 6px;
  background: #D7F0F7;
  min-height: 100px;
  padding: 5px;
  padding-top: 25px;
}

.custom-modal-header {
  padding: 1rem;
  padding-left: 0px;
  padding-right: 0px;
  border-bottom: 1px solid #dee2e6;
  background-color: #f8f9fa;
}

.header-content {
  flex-wrap: wrap;
}

.customer-statistic {
  border-radius: 4.951px;
  border: 0.99px solid #D9D9D9;
  padding: 10px;
  min-height: 150px;
}

/* Device */
@media (max-width: 576px) {
  .header-content {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-actions {
    margin-top: 0.5rem;
    align-self: flex-end;
  }
}

#bv-modal-detail .modal-dialog {
  max-width: 100vw;
  width: 95vw;
  height: 90vh !important;
  overflow-y: hidden;
}

.custom-header {
  padding: 0rem;
  border-bottom: 1px solid #dee2e6;
}

.header-actions .btn {
  padding: 0.25rem 0.5rem;
}

.p-1 {
  margin-left: 20px;
}

.chart {
  width: 100%;
  height: 500px;
}

.closeButton {
  position: absolute;
  right: 20px;
  top: 0px;
}

/* Loader Overlay Styles */
.table-container {
  position: relative;
}

.loader-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #EFF4FD;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  animation: spin 1s linear infinite;
  margin-bottom: 10px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.1s ease-out;
  will-change: opacity, transform;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-20px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
</style>
<script lang="ts">
import { Component, Ref, Watch } from 'vue-property-decorator';
import _ from "lodash";
import moment from "moment";
import BaseComponent from "@/components/BaseComponent";
import { BvTableFieldArray } from "bootstrap-vue";
import { BTable, BvTableCtxObject, BvTableProviderPromiseResult } from "bootstrap-vue/src/components/table";
import * as reportService from "@/service/reportService";
import * as areaService from "@/service/areaService";
import { BounceTimes } from '../enums/TimesNumbers';
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import vi from "vue2-datepicker/locale/vi";
import * as subscriptionPlanService from "@/service/subscriptionPlanService";
import * as customerService from "@/service/customerService";
import { StatusBadgeVariant, StatusText, ReportSelectOptionsEnum, MeetingStatus } from '../enums/MeetingStatus';
import { convertDate, convertSecondsToMinutes, formatMinutesToHhMmSS, getDefaultDateRange, processDateRange, smartBytesToUnit } from '@/service/util';
import * as regionService from '@/service/regionService';
import VueTreeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import  * as util from "@/service/util";

/**
 * ============================================================
 * # COMPONENT META
 * ============================================================
 *
 * Component: OutputStatistic
 * Mục tiêu:
 *  - Hiển thị báo cáo và thống kê (danh sách khách hàng, chi tiết cuộc họp, biểu đồ)
 *  - Hỗ trợ export Excel / PDF
 *  - Filter nâng cao, chart dữ liệu theo khoảng thời gian
 *
 * Lưu ý:
 *  - Mình giữ nguyên toàn bộ logic. Chỉ gom nhóm và thêm comment chi tiết.
 */

@Component({
    components: { DatePicker, VueTreeselect }
})
export default class OutputStatistic extends BaseComponent {

    // ============================================================
    // #region CONSTANTS & INITIAL CONFIG
    // ============================================================
    /**
     * @description Default date range (2 phần tử: [startDate, endDate])
     * @type {[Date, Date]}
     * @readonly
     */
    private readonly defaultRange: [Date, Date] = getDefaultDateRange() as [Date, Date];

    /**
     * @description Enum/Mapping để sử dụng trong template cho badge / text
     */
    readonly statusBadgeVariantEnum = StatusBadgeVariant;
    readonly statusTextEnum = StatusText;

    /**
     * @description Locale cho DatePicker (Tiếng Việt)
     */
    lang = vi;

    // ============================================================
    // #region REACTIVE STATE (DATA)
    // ============================================================
    // Các state chính dùng trong component
    customerDetail = null;
    isLoading = false;
    isLoadingOverviewData = false;
    isExportingToExcel = false;
    isExportingToPDF = false;
    isLoadingMeetingParticipantsCountData = false;
    isLoadingMeetings = false;
    overviewData = null;
    meetingParticipantsCountData = null;
    activeCustomerSubscription = null;
    activeSubscriptionPlan = null;
    customer = null;
    customerSubscriptions: any = [];
    adminUsers = [];

    // UI flags
    showInfo: boolean = true;
    showChart: boolean = true;

    /**
     * ============================================================
     * #region DATE PICKER & FILTER FIELDS
     * ============================================================
     *
     * Các biến bắt đầu bằng `dateRange*` dùng chứa cặp [Date|null, Date|null]
     * - dateRange: chính cho filter chung
     * - dateRangeCreateAccount: filter theo ngày tạo tài khoản (advance)
     * - dateRangeMeetings: filter theo ngày họp
     * - dateRangeDetailFilter: filter dùng cho modal detail / chart
     */
    dateRange: [Date | null, Date | null] = [...this.defaultRange];
    dateRangeCreateAccount: [Date | null, Date | null] = [...this.defaultRange];
    dateRangeMeetings: [Date | null, Date | null] = [...this.defaultRange];
    dateRangeDetailFilter: [Date | null, Date | null] = [...this.defaultRange];

    // Chart filter (tách ra để bind vào form)
    chartFilterStartDate: Date = null;
    chartFilterEndDate: Date = null;
    chartFilterFormErrorMsg = "";

    // Advance Filter fields
    decreeChoose = 0;
    selectedOption: string | null = null; // Biến cho sắp xếp
    saleAreaOption: string | null = null; // Khu vực kinh doanh
    areaOption: string | null = null; // Khu vực
    unitOption: string | null = null; // Đơn vị phòng ban
    packageOption: string | null = null; // Gói cước
    searchText = ""; // Chuỗi tìm kiếm
    searchAreaId: number = null;
    formErrorMsg = "";
    subscriptionPlanId: number = null;
    subscriptionPlans = [];
    subPlansOverView = [];
    featureString = "";
    // Table Filter (Meetings)
    filterStatus: string = null;
    isShowChart: boolean = false;

    /**
     * @description Cờ để phân biệt khi bật search nâng cao.
     * Nếu true thì request sẽ gửi thêm ngày tạo tài khoản.
     */
    isAdvanceSearch: boolean = false;

    // ============================================================
    // #region SELECT OPTIONS / UI OPTIONS
    // ============================================================
    selectOptions = [
        { value: ReportSelectOptionsEnum.MEETING_COUNT, text: this.$t("numMeeting") },
        { value: ReportSelectOptionsEnum.MEETING_DURATION, text: this.$t("numDuration") },
        { value: ReportSelectOptionsEnum.STORAGE_CAPACITY, text: this.$t("storageCapacity") },
        { value: ReportSelectOptionsEnum.STORAGE_USED, text: this.$t("usedStotage") },
        { value: ReportSelectOptionsEnum.SUMMARY_DURATION, text: this.$t("timeSumaration") }
    ];
    saleAreaOptions = [];
    areaOptions = [];
    unitOptions = [];
    packageOptions = [];
    searchAreaOptions = [];
    itemsPerPageSelections = [
        { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
        { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
        { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
        { value: 200, text: "200 " + this.$t("itemsPerPage").toString() },
    ];

    // ============================================================
    // #region REFS (Template refs)
    // ============================================================
    @Ref("table") table: BTable;
    @Ref("table_Meetings") table_Meetings: BTable;
    @Ref('chartRef') chartRef!: any;

    // ============================================================
    // #region PAGINATION STATE
    // ============================================================
    // General Table
    itemsPerPage = 20;
    currentPage = 1;
    totalItems = 0;

    // Meetings Table
    itemsPerPage_Meetings = 20;
    currentPage_Meetings = 1;
    totalItems_Meetings = 0;

    // ============================================================
    // #region TABLE FIELDS (Cấu hình cột cho bootstrap-vue table)
    // ============================================================
    tableFields: BvTableFieldArray = [
        { key: "Id", label: "ID", thClass: "text-nowrap", tdClass: "text-nowrap", sortable: false },
        { key: "customerInfo", label: this.$t("customer").toString(), thClass: "text-nowrap", tdClass: "text-break", sortable: false },
        { key: "saleAreaName", label: this.$t("saleAreaShort").toString(), thClass: "text-nowrap", tdClass: "text-break", sortable: false },
        { key: "unitName", label: this.$t("unitArea").toString(), thClass: "text-nowrap", tdClass: "text-break", sortable: false, thStyle: { width: '120px' } },
        { key: "subscriptionPlan", label: this.$t("subscriptionPlan").toString(), thClass: "text-nowrap", tdClass: "text-break", sortable: false },
        { key: "cusSubEndDate", label: this.$t("expDate").toString(), thClass: "text-nowrap", tdClass: "text-break", sortable: false },
        { key: "meetingStats", label: this.$t("meeting").toString(), thClass: "text-nowrap", tdClass: "text-break", sortable: false },
        { key: "recordingStats", label: this.$t("recordingStats").toString(), thClass: "text-nowrap", tdClass: "text-break", sortable: false },
        { key: "totalRecordingSize", label: this.$t("aiGen").toString(), thClass: "text-nowrap", tdClass: "text-nowrap", sortable: false },
        { key: "actions", label: "", thClass: "text-nowrap", tdClass: "text-nowrap" }
    ];

    tableFields_Meetings: BvTableFieldArray = [
        { key: "id", label: "ID", thClass: "text-nowrap" },
        { key: "name", label: this.$t("meeting").toString() },
        { key: "startTime", label: this.$t("meetingDate").toString(), thClass: "text-nowrap", tdClass: "text-nowrap" },
        { key: "recordingSize", label: this.$t("record").toString(), thClass: "text-nowrap" },
        { key: "usedSummary", label: this.$t("summary").toString(), thClass: "text-nowrap" },
        { key: "subscriptionPlanName", label: this.$t("subscriptionPlan").toString(), thClass: "text-nowrap" },
        { key: "createdAt", label: this.$t("createDate").toString(), thClass: "text-nowrap", tdClass: "text-nowrap" },
        //{ key: "deletedByName", label: this.$t("deleteBy").toString(), thClass: "text-nowrap" },
        { key: "meetingStatus", label: this.$t("status").toString(), thClass: "text-nowrap" },
    ];

    // ============================================================
    // #region CHART CONFIG (ECharts options)
    // ============================================================
    chartDetailOptions = {
        legend: { top: 36, },
        tooltip: { trigger: 'axis', axisPointer: { type: 'cross' }, },
        grid: { top: 100, left: '5%', right: '5%', bottom: '10%', containLabel: true, },
        xAxis: { type: 'category', data: [], axisLabel: { rotate: 0, }, },
        yAxis: [
            { type: 'value', name: this.$t("meeting").toString(), position: 'left', nameTextStyle: { fontWeight: "bold" }, axisLine: { show: true }, axisLabel: { formatter: '{value}' } },
            { type: 'value', name: this.$t("meetingParticipant").toString(), position: 'right', nameTextStyle: { fontWeight: "bold" }, axisLine: { show: true }, axisLabel: { formatter: '{value}' }, offset: 10, },
        ],
        series: [
            { name: this.$t("meeting").toString(), type: 'bar', yAxisIndex: 0, barWidth: '25%', itemStyle: { color: '#3B82F6' }, data: [], barGap: '0%' },
            { name: this.$t("meetingParticipant").toString(), type: 'bar', yAxisIndex: 1, barWidth: '25%', itemStyle: { color: '#10B981' }, data: [] },
            { name: this.$t("record").toString(), type: 'line', smooth: true, lineStyle: { width: 2, color: '#F59E0B' }, symbol: 'circle', symbolSize: 8, itemStyle: { color: '#F59E0B' }, data: [] },
            { name: this.$t("aiGen").toString(), type: 'line', smooth: true, lineStyle: { width: 2, color: '#FF4D00' }, symbol: 'circle', symbolSize: 8, itemStyle: { color: '#FF4D00' }, data: [] },
        ],
    };

    // Overview items (hiển thị nhanh)
    overviewItems = [];

    // ============================================================
    // #region COMPUTED PROPERTIES
    // ============================================================
    /**
     * @returns active subscription object nếu có
     */
    get activeSubscription() {
        return this.customerSubscriptions?.find(it => it.isActive);
    }

    get startDate() {
        return this.dateRange?.[0] || null;
    }

    get endDate() {
        return this.dateRange?.[1] || null;
    }

    get startDateMeetings() {
        return this.dateRangeMeetings?.[0] || null;
    }

    get endDateMeetings() {
        return this.dateRangeMeetings?.[1] || null;
    }

    get startDateCreateAccount() {
        return this.dateRangeCreateAccount?.[0] || null;
    }

    get endDateCreateAccount() {
        return this.dateRangeCreateAccount?.[1] || null;
    }

    get startDateFilter() {
        return this.dateRange?.[0] || null;
    }

    get endDateFilter() {
        return this.dateRange?.[1] || null;
    }

    get startDateDetail() {
        return this.dateRangeDetailFilter?.[0] || null;
    }

    get endDateDetail() {
        return this.dateRangeDetailFilter?.[1] || null;
    }

    /**
     * @description Trả về option list cho status select (dùng trong UI)
     * @returns Array<{value, text}>
     */
    get statusOptions() {
        return [
            { value: null, text: this.$t("all").toString() },
            { value: MeetingStatus.PENDING, text: this.$t("meetingStatusPending").toString() },
            { value: MeetingStatus.INPROGRESS, text: this.$t("inprogress").toString() },
            { value: MeetingStatus.END, text: this.$t("meetingStatusEnded").toString() },
            { value: MeetingStatus.CANCEL, text: this.$t("cancelled").toString() }
        ];
    }

    getLabelForRecord(dataItem)
    {
      
      return util.getLabelForRecord(dataItem,this.$t("yes").toString(),this.$t("no").toString());


    }
    getLabelForSummary(dataItem)
    {

       return util.getLabelForSum(dataItem,this.$t("yes").toString(),this.$t("no").toString());
    }

    getConvertSize(data)
    {
      return util.smartBytesToUnit(data);;
    }
    // ============================================================
    // #region WATCHERS
    // ============================================================
    /**
     * @watch meetingParticipantsCountData
     * @description Khi meetingParticipantsCountData thay đổi thì cập nhật chartDetailOptions.
     *              immediate: true, deep: true
     * @param meetingParticipantsCountData - mảng dữ liệu chart (mỗi phần tử có scheduleDate, totalMeetings, totalParticipants, totalUsedSummary, totalVoices)
     */
    @Watch("meetingParticipantsCountData", { immediate: true, deep: true })
    onMeetingParticipantsCountDataChange(meetingParticipantsCountData) {
        if (meetingParticipantsCountData) {
            this.chartDetailOptions.xAxis.data = meetingParticipantsCountData.map(it => it.scheduleDate);
            this.chartDetailOptions.series[0].data = meetingParticipantsCountData.map(it => it.totalMeetings);
            this.chartDetailOptions.series[1].data = meetingParticipantsCountData.map(it => it.totalParticipants);
            this.chartDetailOptions.series[2].data = meetingParticipantsCountData.map(it => it.totalRecordedMeetings);
            this.chartDetailOptions.series[3].data = meetingParticipantsCountData.map(it => it.totalUsedSummary);
        }
    }

    // ============================================================
    // #region LIFECYCLE HOOKS
    // ============================================================
    /**
     * @description mounted hook
     * - Setup initial chart filter dates
     * - Fetch dropdown/select data và các danh sách ban đầu
     */
  async mounted() {
    try {
      this.isLoading = true;
      this.dateRangeCreateAccount = null;
      [this.chartFilterStartDate, this.chartFilterEndDate] = this.dateRangeDetailFilter;
      await Promise.all([
        this.fetchAreas(),
        this.fetchData(),
        this.fetchLocation(),
        this.fetchSubscriptionPlans(),
        this.fetchUnitArea(),
        this.fetchAllAreas(),
      ]);
    } catch (err) {
      console.error("Error initializing component:", err);
    } finally {
      this.isLoading = false;
    }
  }

    // ============================================================
    // #region METHODS (API, UI, EXPORT, HELPERS)
    // ============================================================
    // ----------------------
    // Section: API Calls & Data Fetching
    // ----------------------

    /**
     * @description Data provider cho bảng chính (dùng BvTable provider)
     * @param ctx - BvTable context (chứa currentPage, perPage etc.)
     * @returns Promise<BvTableProviderPromiseResult> - dữ liệu bảng (mảng)
     *
     * Lưu ý: Không thay đổi logic, chỉ validate range và gọi reportService.getOutputStatistic
     */
    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
        this.formErrorMsg = "";

        // Kiểm tra ngày cơ bản
        if (this.startDate && this.endDate && this.startDate.getTime() > this.endDate.getTime()) {
            this.formErrorMsg = this.$t("invalidDateRange").toString();
            return [];
        }

        this.isLoading = true;
        await new Promise(resolve => setTimeout(resolve, 300));

        try {
            // Format date time
            const { fromDate, toDate } = processDateRange(this.startDateCreateAccount, this.endDateCreateAccount);
            // Lấy khoảng ngày họp
            const { fromDate: fromDateMeetings, toDate: toDateMeetings } = processDateRange(this.startDateMeetings, this.endDateMeetings);
            const outputStatistic = await reportService.getOutputStatistic({
                fromDate,// Ngày tạo tài khoản từ ngày
                toDate,// Ngày tạo tài khoản đến ngày
                meetingFromDate: fromDateMeetings, //ngày họp từ ngày
                meetingToDate: toDateMeetings, // ngày họp đến ngày
                // page
                page: ctx.currentPage,
                pageSize: this.itemsPerPage,
                // tìm kiếm
                q: this.searchText?.trim(),
                // lọc nâng cao
                saleAreaId: this.searchAreaId,
                provinceCode: this.areaOption || null,
                subPlanId: this.subscriptionPlanId || null,
                sortField: this.selectedOption || null
            }, 
            this.unitOption || null,
            this.isAdvanceSearch);
            this.totalItems = outputStatistic.total;
            return outputStatistic.data;
        } catch (e) {
            this.toastError(e);
            return [];
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * @description Data provider cho bảng Meetings (trong modal detail)
     * @param ctx - BvTable context
     * @returns Promise<BvTableProviderPromiseResult>
     *
     * Lưu ý: Check date range và gọi reportService.getMeetingListByCustomerId
     */
    async tableItemProvider_Meetings(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
        this.isLoadingMeetings = true; // Bật loading cho bảng cuộc họp
        try {
            if (this.startDateDetail && this.startDateDetail && this.startDateDetail.getTime() > this.startDateDetail.getTime()) {
                this.formErrorMsg = this.$t("invalidDateRange").toString();
                return [];
            }
            const { fromDate, toDate } = processDateRange(this.startDateDetail, this.endDateDetail);

            const res = await reportService.getMeetingListByCustomerId(this.customerDetail.id, {
                meetingStatus: this.filterStatus,
                page: ctx.currentPage,
                pageSize: ctx.perPage
            }, fromDate, toDate);
            this.totalItems_Meetings = res.total;
            return res.data;
        } catch (e) {
            this.toastError(e);
            return [];
        } finally {
            this.isLoadingMeetings = false; // Tắt loading cho bảng cuộc họp
        }
    }

    // ----------------------
    // Section: UI helpers (chart resize, modal show)
    // ----------------------

    /**
     * @description Detect when modal detail is shown. Resize chart nếu cần.
     * @returns void
     */
    onModalShown() {
        this.$nextTick();
        if (this.chartRef && typeof this.chartRef.resize === 'function') {
            this.chartRef.resize();
        } else {
            console.warn("Chart ref not ready or resize method missing after delay.");
        }
    }

    // ----------------------
    // Section: Chart data fetchers
    // ----------------------

    /**
     * @description Lấy dữ liệu chart theo customer id và khoảng thời gian đã chọn.
     * @param data - đối tượng khách hàng (phải có id)
     * @returns Promise<void>
     *
     * Behavior:
     *  - Kiểm tra ngày start/end hợp lệ
     *  - Gọi reportService.getChartDataByCustomerId(fromDate, toDate, customerId)
     *  - Cập nhật this.meetingParticipantsCountData và this.isShowChart
     *  - Cuối cùng show modal detail (this.$bvModal.show('bv-modal-detail'))
     */
    async fetchMeetingParticipantsCountData(data) {
        this.chartFilterFormErrorMsg = "";
        if (this.chartFilterStartDate == null || this.chartFilterEndDate == null) {
            this.isShowChart = false;
            return;
        }

        if (this.chartFilterStartDate.getTime() > this.chartFilterEndDate.getTime()) {
            this.isShowChart = false;
            this.chartFilterFormErrorMsg = this.$t("invalidDateRange").toString();
            return;
        }

        let fromDate = null;
        let toDate = null;

        if (this.chartFilterStartDate !== null && this.chartFilterEndDate !== null) {
            let startDate = new Date(this.chartFilterStartDate);
            let endDate = new Date(this.chartFilterEndDate);
            const processedDates = processDateRange(startDate, endDate);
            fromDate = processedDates.fromDate;
            toDate = processedDates.toDate;
        }

        try {
            this.isLoadingMeetingParticipantsCountData = true;
            this.meetingParticipantsCountData = await reportService.getChartDataByCustomerId(fromDate, toDate, data.id.toString());
            this.isShowChart = this.meetingParticipantsCountData.length > 0;
        } catch (e) {
            this.toastError(e);
        } finally {
            this.isLoadingMeetingParticipantsCountData = false;
            this.$bvModal.show('bv-modal-detail');
        }
    }

    // ----------------------
    // Section: Overview (summary) data
    // ----------------------

    /**
     * @description Lấy dữ liệu overview (số liệu tóm tắt) cho customer
     * @param data - đối tượng customer (phải có id)
     * @returns Promise<void>
     *
     * Behavior:
     *  - Build fromDate/toDate từ chartFilterStartDate/chartFilterEndDate nếu có
     *  - Gọi reportService.getOutputStatictisOverView(fromDate, toDate, customerId)
     *  - Map kết quả vào overviewItems (dùng cho giao diện)
     */
    async fetchOverview(data) {
        try {
            this.isLoadingOverviewData = true;
            //
            let fromDate = null;
            let toDate = null;

            if (this.chartFilterStartDate !== null && this.chartFilterEndDate !== null) {
                let startDate = new Date(this.chartFilterStartDate);
                let endDate = new Date(this.chartFilterEndDate);
                const processedDates = processDateRange(startDate, endDate);
                fromDate = processedDates.fromDate;
                toDate = processedDates.toDate;
            }

            this.overviewData = await reportService.getOutputStatictisOverView(fromDate, toDate, data.id.toString());
            const jsonString = JSON.stringify(this.overviewData);
            const rawArray = JSON.parse(jsonString);
            this.overviewItems = [
                { text: this.$t("Id").toString(), key: "subscriptionPlanId", value: rawArray.subscriptionPlanId, bgColor: "#D7F0F7" },
                { text: this.$t("subPlanName").toString(), key: "subPlanName", value: rawArray.subscriptionPlanName, bgColor: "#D7F0F7" },
                { text: this.$t("expDate").toString(), key: "expDate", value: this.convertDateOverView(rawArray.subscriptionEndDate), bgColor: "#D7F0F7" },
                { text: this.$t("totalMeeting").toString(), key: "totalMeeting", value: rawArray.totalMeetings, bgColor: "#D7F0F7" },
                { text: this.$t("numDuration").toString() , key: "duration", value: this.convertToHHMMSS(rawArray.totalMeetingsDuration), bgColor: "#D7F0F7" },
                { text: this.$t("totalMeetingsRecorded").toString(), key: "totalMeetingsRecorded", value: (this.getCurrenRecord(rawArray)), bgColor: "#D7F0F7" },
                { text: this.$t("aiGen").toString(), key: "aiGen", value: (rawArray.totalUsedSummary + " " + this.$t("CH")), bgColor: "#D7F0F7" },
                { text: this.$t("summaryDuration").toString() , key: "summaryDuration", value: this.convertToHHMMSS(rawArray.totalSummaryDuration), bgColor: "#D7F0F7" },
                { text: this.$t("saleArea2").toString(), key: "saleArea2", value: rawArray.areaName, bgColor: "#D7F0F7" },
            ];
        } catch (e) {
            this.toastError(e);
        } finally {
            this.isLoadingOverviewData = false;
        }
    }

    /**
     * @description Lấy label cho phần "Ghi hình" (record info)
     * @param infor - object chứa totalRecordedMeetings, totalRecordingSize
     * @returns string - chuỗi hiển thị (ví dụ "2 lần | 1.5GB")
     */
    getCurrenRecord(infor: any) {
        if (!infor) return this.$t("noData");
        let strCurrentRetunr = "";
        if (infor.totalRecordedMeetings) strCurrentRetunr += infor.totalRecordedMeetings.toString() + " " + this.$t("timeCount");;
        if (infor.totalRecordingSize) strCurrentRetunr += " | " + smartBytesToUnit(infor.totalRecordingSize);
        return strCurrentRetunr;
    }

    // ----------------------
    // Section: Customer & dropdown data fetch
    // ----------------------

    /**
     * @description Lấy chi tiết customer và subscription list cho customer đó
     * @param idCustomer - id của customer
     * @returns Promise<void>
     *
     * Behavior:
     *  - Gọi customerService.getDetails và subscriptionPlanService.getCustomerSubscription
     *  - Gán vào this.customer, this.subscriptions, this.subPlansOverView
     */
    async fetchCustomer(idCustomer: number) {
        try {
            const customerInfomation = customerService.getDetails(idCustomer);
            const customserSubscriptions= subscriptionPlanService.getCustomerSubscription(idCustomer);
            this.customer = await customerInfomation;
            this.customerSubscriptions = await customserSubscriptions;
            this.subPlansOverView = this.customerSubscriptions;
        } catch (e) {
            this.toastError(e);
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * @description Lấy danh sách area cho search filter (dùng cho searchAreaOptions)
     * @returns Promise<void>
     */
    async fetchAreas() {
        try {
            const areas = await areaService.getAll();
            if (!areas) return;
            this.searchAreaOptions = areas.map(a => ({ value: a.id, text: a.name }));
            this.searchAreaOptions.unshift({ value: null, text: this.$t("all").toString() });
        } catch (e) {
            this.toastError(e);
        }
    }

    /**
     * @description Lấy danh sách 34 tỉnh thành (regionService.getAllAreaList)
     * @returns Promise<void>
     */
    async fetchAllAreas() {
        try {
            let allAreas = await regionService.getAllAreaList();
            allAreas = allAreas.data;
            if (!allAreas) return;
            this.areaOptions = allAreas.map(a => ({ value: a.currentCode, text: a.currentName }));
            this.areaOptions.unshift({ value: null, text: this.$t("all").toString() });
        } catch (e) {
            this.toastError(e);
        }
    }

    /**
     * @description Lấy danh sách khu vực kinh doanh (regionService.getRegionList)
     * @returns Promise<void>
     */
    async fetchLocation() {
        let region = await regionService.getRegionList({
            page: 1,
            pageSize: 500,
            q: "",
            name: ""
        });
        if (!region) return;
        region = region.data;
        this.saleAreaOptions = region.map(a => ({ value: a.id, text: a.name }));
        this.saleAreaOptions.unshift({ value: null, text: this.$t("all").toString() });
    }

    /**
     * @description Lấy danh sách subscription plans (toàn bộ plans)
     * @returns Promise<void>
     */
    async fetchSubscriptionPlans() {
        try {
            let plans = await subscriptionPlanService.getPlans();
            this.subPlansOverView = plans;
            if (!plans) return;
            this.subscriptionPlans = plans.map(a => ({ value: a.id, text: a.name }));
            this.subscriptionPlans.unshift({ value: null, text: this.$t("all").toString() });
        } catch (e) {
            this.toastError(e);
        }
    }

    /**
     * @description Fetch areas duplicated (theo code gốc) — có comment nói duplicated
     * @returns Promise<void>
     */
    async fetchData() {
        try {
            const areas = await areaService.getAll();
            if (areas) return;
            this.areaOptions = areas.map(a => ({ value: a.id, text: a.name }));
            this.areaOptions.unshift({ value: null, text: this.$t("all").toString() });
        } catch (e) {
            this.toastError(e);
        }
    }

    /**
     * @description Fetch unit area list. Logic: nếu response.data.data là array thì gán, nếu object thì bọc vào mảng.
     * @returns Promise<void>
     */
    async fetchUnitArea() {
        try {
            const response = await regionService.getUnitAreaList();
            const dataRoot = response.data.data;
            if (Array.isArray(dataRoot) && dataRoot.length > 0) {
                this.unitOptions = dataRoot;
            } else if (dataRoot && typeof dataRoot === 'object') {
                this.unitOptions = [dataRoot];
            } else {
                this.unitOptions = [];
            }
        } catch (error) {
            console.error("❌ Lỗi khi fetchUnitArea:", error);
            this.unitOptions = [];
        }
    }

    // ----------------------
    // Section: Helpers / Normalizer
    // ----------------------

    /**
     * @description Normalizer cho VueTreeselect tree data (map node -> {id, label, children})
     * @param node - node raw từ API
     * @returns {id, label, children} | null
     */
    normalizer(node) {
        if (!node || !node.unit) return null;
        return {
            id: node.unit.unitCode,
            label: node.unit.unitName,
            children: node.children?.length ? node.children : undefined,
        };
    }

    // ----------------------
    // Section: Date handlers
    // ----------------------

    /**
     * @description Handle khi thay đổi date range trên detail filter.
     * @param newDateRange - [Date|null, Date|null]
     */
    handleDateChange(newDateRange) {
        if (newDateRange == null) return;
        this.dateRangeDetailFilter = newDateRange;
        this.chartFilterStartDate = newDateRange[0];
        this.chartFilterEndDate = newDateRange[1];
        this.dateRangeMeetings = newDateRange;
    }

    /**
     * @description Handle date change cho dateRangeCreateAccount
     * @param newDateRange
     */
    handleDateChangeCreateAccount(newDateRange) {
        this.dateRangeCreateAccount = newDateRange;
    }

    /**
     * @description Handle date change cho meeting date range
     * @param newDateRange
     */
    handleDateChangeMeetings(newDateRange) {
        this.dateRangeMeetings = newDateRange;
        this.dateRange = newDateRange;
    }

    /**
     * @description Khi chọn status filter -> refresh bảng meetings
     */
  async onStatusSelect() {
    this.isLoading = true;
    await this.table_Meetings.refresh();
    this.isLoading = false;
  }

    /**
     * @description Click search ở page chính -> refresh bảng chính
     */
    onClickSearchBtn() {
        this.table.refresh();
    }

    // ----------------------
    // Section: Debounced actions
    // ----------------------

    /**
     * @description Debounce cho nút tìm kiếm chính để tránh spam request
     */
    debounceOnClickSearchBtn = _.debounce(() => {
        this.onClickSearchBtn();
    }, BounceTimes.BOUNCETIMES);

    /**
     * @description Debounce cho search nâng cao -> hide modal filter rồi refresh
     */
    debounceOnClickAdvanceSearchBtn = _.debounce(() => {
        this.$bvModal.hide('bv-modal-filter');
        this.onClickSearchBtn();
    }, BounceTimes.BOUNCETIMES);

    /**
     * @description Debounce cho xuất Excel (main)
     */
    debouncenOnClickExportToExcel = _.debounce(() => {
        this.onClickExportToExcel();
    }, BounceTimes.BOUNCETIMES);

    // ----------------------
    // Section: Export to Excel (main)
    // ----------------------

    /**
     * @description Xuất báo cáo chính ra file Excel
     * @returns Promise<void>
     *
     * Quy trình:
     *  - Validate date range
     *  - Chuẩn bị translations (i18n)
     *  - Chuẩn bị params (fromDate, toDate, meetingFromDate...)
     *  - Gọi reportService.exportOutputStatisticToExcel(params, translations)
     */
    async onClickExportToExcel() {
        try {
            this.isExportingToExcel = true;
            // Kiểm tra ngày
            if (this.startDate && this.endDate && this.startDate.getTime() > this.endDate.getTime()) {
                this.formErrorMsg = this.$t("invalidDateRange").toString();
                return;
            }
            // Chuẩn bị các bản dịch
            const translations = {
                meetingStatistics: this.$t("meetingStatistics").toString(),
                fromDate: this.$t("fromDate").toString(),
                toDate: this.$t("toDate").toString(),
                IdKH: this.$t("IdKH").toString(),
                name: this.$t("name").toString(),
                phoneNumber: this.$t("phoneNumber").toString(),
                email: this.$t("email").toString(),
                address: this.$t("address").toString(),
                saleArea: this.$t("saleArea").toString(),
                unitDepart: this.$t("unitDepart").toString(),
                thoidiemtao: this.$t("thoidiemtao").toString(),
                goicuoc: this.$t("goicuoc").toString(),
                songayconlai: this.$t("songayconlai").toString(),
                totalMeetings: this.$t("totalMeetings").toString(),
                totalActualDuration: this.$t("totalActualDuration").toString(),
                totalMeetingsRecorded: this.$t("totalMeetingsRecorded").toString(),
                totalRecordingSize: this.$t("totalRecordingSize").toString(),
                totalMeetingsSummary: this.$t("totalMeetingsSummary").toString(),
                invalidDateRange: this.$t("invalidDateRange").toString(),
            };
            // Chuẩn bị tham số
            const params = {
                fromDate: this.startDate,
                toDate: this.endDate,
                meetingFromDate: this.startDateMeetings,
                meetingToDate: this.endDateMeetings,
                q: this.searchText,
                saleAreaId: this.searchAreaId,
                unitCode: this.unitOption,
                areaOption: this.areaOption
                
            };

            // Gọi service để xuất Excel
            await reportService.exportOutputStatisticToExcel(params, translations);

        } catch (e) {
            this.toastError(e);
        } finally {
            this.isExportingToExcel = false;
        }
    }

    // ----------------------
    // Section: Export to PDF (full page)
    // ----------------------

    debouncenExportFullPageToPDF = _.debounce(() => {
        this.exportFilePDF();
    }, BounceTimes.BOUNCETIMES);

    /**
     * @description Xuất toàn bộ nội dung modal detail/full page ra PDF
     * @returns Promise<void>
     *
     * Quy trình (tóm tắt):
     *  - Ẩn các phần không cần xuất (.no-export, nút export)
     *  - Lấy toàn bộ dữ liệu meetings (paging loop)
     *  - Chụp element .export-area bằng html2canvas (scale:2)
     *  - Gọi reportService.exportFullPageToPDF với canvas image data
     */
    async exportFilePDF() {
        this.isExportingToPDF = true;
        try {
            // --- Ẩn các phần không cần xuất ---
            const button = document.querySelector('.export-fullpage-btn') as HTMLElement | null;
            if (button) button.style.display = 'none';
            const noExportElements = document.querySelectorAll('.no-export');
            noExportElements.forEach(el => (el as HTMLElement).style.display = 'none');

            const element = document.querySelector('.export-area') as HTMLElement;
            if (!element) {
                console.error('Không tìm thấy .export-area để xuất PDF');
                return;
            }

            await this.$nextTick();

            // --- Gom toàn bộ dữ liệu bảng ---
            const pageSize = 1000;
            let currentPage = 1;
            let allMeetings: any[] = [];
            let total = 0;

            do {
                const res = await reportService.getMeetingListByCustomerId(
                    this.customerDetail.id,
                    {
                        meetingStatus: this.filterStatus,
                        page: currentPage,
                        pageSize
                    },
                    this.startDateDetail?.toISOString(),
                    this.endDateDetail?.toISOString()
                );

                const data = res.data || [];
                total = res.total || 0;
                allMeetings = allMeetings.concat(data);
                if (data.length < pageSize) break;
                currentPage++;
            } while (allMeetings.length < total);

            // --- Chụp phần biểu đồ, tiêu đề, v.v. bằng html2canvas ---
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                scrollY: 0,
                scrollX: 0,
                backgroundColor: '#ffffff', // Đảm bảo nền trắng
                width: element.scrollWidth,
                height: element.scrollHeight
            });

            // --- Dọn dẹp ---
            const tempTitle = document.querySelector('.pdf-temp-title');
            if (tempTitle) tempTitle.remove();
            if (button) button.style.display = 'block';
            noExportElements.forEach(el => (el as HTMLElement).style.display = '');

            // Chuẩn bị các chuỗi đã dịch
            const statusTexts = {
                meeting: this.$t('meeting').toString(),
                meetingDate: this.$t('meetingDate').toString(),
                capacity: this.$t('capacity').toString(),
                aiGen: this.$t('aiGen').toString(),
                package: this.$t('package').toString(),
                createBy: this.$t('createBy').toString(),
                deleteBy: this.$t('deleteBy').toString(),
                status: this.$t('status').toString(),
                END: this.$t('meetingStatusEnded').toString(),
                INPROGRESS: this.$t('inprogress').toString(),
                PENDING: this.$t('meetingStatusPending').toString(),
                CANCEL: this.$t('cancelled').toString(),
            };

            const title = this.$t("outputStatisticTitle", { NAME: this.customerDetail.name }).toString();

            // Gọi service để xuất PDF
          await reportService.exportFullPageToPDF(
            this.customerDetail,
            allMeetings,
            statusTexts,
            title,
            canvas.toDataURL('image/png', 1),
            canvas.width,
            canvas.height,
            this.$t("TG").toString(),
            this.$t("TL").toString(),
            this.$t("TGia").toString(),
            this.$t("perMeeting").toString(),
            this.$t("meetDay").toString(),
            this.$t("storage").toString()

          );

        } catch (e) {
            console.error(e);
            this.toastErrorOldType(e);
        } finally {
            this.isExportingToPDF = false;
        }
    }

    // ----------------------
    // Section: Detail modal actions (search, export)
    // ----------------------

    /**
     * @description Debounced search trong modal detail -> refresh data
     */
    debounceSearchFromDetail = _.debounce(() => {
        this.searchFromDetail();
    }, BounceTimes.BOUNCETIMES);

    /**
     * @description Thực hiện refresh bảng meetings trong modal detail và load lại data detail
     */
    async searchFromDetail() {
        this.table_Meetings.refresh();
        if (!this.customerDetail) return;
        this.loadDataDetail(this.customerDetail);
    }

    /**
     * @description Debounce cho export Excel danh sách meetings từ modal detail
     */
    debounceExportExcellMeetingsList = _.debounce(() => {
        this.exportExcellMeetingsList();
    }, BounceTimes.BOUNCETIMES);

    /**
     * @description Export danh sách meetings từ modal detail ra Excel
     * @returns Promise<void>
     */
    async exportExcellMeetingsList() {
        try {
            this.isExportingToExcel = true;

            // Chuẩn bị các bản dịch
            const translations = {
                meetingList: this.$t("meetingList").toString(),
                meetingName: this.$t("meetingName").toString(),
                createDate: this.$t("createDate").toString(),
                meetingDate: this.$t("meetingDate").toString(),
                meetingTime: this.$t("meetingTime").toString(),
                duration: this.$t("duration").toString(),
                participantCount: this.$t("participantCount").toString(),
                package: this.$t("package").toString(),
                allowedCapacity: this.$t("allowedCapacity").toString(),
                recordingCapacity: this.$t("recordingCapacity").toString(),
                summaryDuration: this.$t("summaryDuration").toString(),
                IdKH: this.$t("IdKH").toString(),
                creatorAccount: this.$t("creatorAccount").toString(),
                status: this.$t("status").toString(),
                deletedAccount: this.$t("deletedAccount").toString()
            };

            // Gọi service để xuất Excel
            await reportService.exportMeetingsListToExcel(
                this.customerDetail.id,
                this.customerDetail.name,
                this.filterStatus,
                this.startDateDetail,
                this.endDateDetail,
                translations,
                this.getStatusTranslate
            );

        } catch (e) {
            console.error(e);
            this.toastError(e);
        } finally {
            this.isExportingToExcel = false;
        }
    }

    // ----------------------
    // Section: Utility & Helpers
    // ----------------------

    /**
     * @description Handler khi date range detail thay đổi -> cập nhật chart filter và fetch chart data
     * @param newDateRange - [Date|null, Date|null]
     */
    onDateRangeDetailChange(newDateRange: [Date | null, Date | null]) {
        if (newDateRange == null) return;
        this.chartFilterStartDate = newDateRange?.[0] || null;
        this.chartFilterEndDate = newDateRange?.[1] || null;
        this.fetchMeetingParticipantsCountData(this.customerDetail);      
    }

    /**
     * @description Dịch status text (map key -> localized string)
     * @param statusText - key status (ví dụ 'END', 'INPROGRESS'...) hoặc text để i18n
     * @returns string
     */
    getStatusTranslate(statusText) {
        return this.$t(statusText).toString();
    }

    /**
     * @description Submit filter (đóng modal filter và refresh table)
     */
    submitFilter() {
        this.$bvModal.hide('bv-modal-filter');
        this.table.refresh;
    }

    /**
     * @description Convert bytes -> friendly string (GB, MB...) dùng smartBytesToUnit
     * @param data - number | null
     * @returns string - chuỗi đã format hoặc "" nếu không có
     */
    convertToGB(data: any) {
        if (data == null || data == undefined) return "";
        var strReturn = smartBytesToUnit(data, true).toString();
        return strReturn;
    }

    /**
     * @description Convert minutes -> HH:MM:SS
     * @param data - number (minutes)
     * @returns string formatted
     */
    convertToHHMMSS(data: number) {
        return formatMinutesToHhMmSS(data);
    }

    /**
     * @description Check meeting platform có phải BBB không
     * @param meeting - meeting object
     * @returns boolean
     */
    isBBB(meeting): boolean {
        return meeting?.corePlatform?.toLowerCase() === "bbb";
    }

    // ----------------------
    // Section: Modal Handlers (filter, clear)
    // ----------------------

    /**
     * @description Mở filter modal (set isAdvanceSearch true)
     */
    showFilter() {
        this.isAdvanceSearch = true; // bật search nâng cao
        this.$bvModal.show('bv-modal-filter');
    }

    /**
     * @description Reset toàn bộ filter về mặc định (bao gồm date range và các option)
     */
    clearFilter() {
        // 👉 Reset tất cả filter về mặc định
        this.searchAreaId = null;
        this.areaOption = null;
        this.unitOption = null;
        this.subscriptionPlanId = null;
        this.selectedOption = null;
        this.table.refresh();
        this.isAdvanceSearch = false;
        this.searchText = "";
        //👉 Reset date range
        this.dateRange = [...this.defaultRange];
        this.dateRangeCreateAccount = [...this.defaultRange];
        this.dateRangeMeetings = [...this.defaultRange];
        this.dateRangeDetailFilter = [...this.defaultRange];
    }

    // ----------------------
    // Section: Hover / Feature tooltip
    // ----------------------

    debouncehandleHover= _.debounce(() => {
        this.handleHover();
    }, BounceTimes.BOUNCETIMES);

    /**
     * @description Lấy featureString để hiển thị tooltip (list feature của subscription plan)
     * Behavior:
     *  - Lấy subscriptionPlanId từ overviewItems
     *  - Tìm plan trong subPlansOverView
     *  - Map features -> chuỗi HTML (check + tên feature)
     */
    handleHover() {
        this.featureString = "........";
        // Lấy subPlanId từ mảng overviewItems
        const subPlanIdItem = this.overviewItems.find(item => item.key === 'subscriptionPlanId');
        const dataFeature = this.subPlansOverView.find(p => p.id == subPlanIdItem.value);

        if (!dataFeature) {
            this.featureString = "";
            return;
        }

        this.featureString =
            dataFeature.features?.map(f => `&#10003; ${f.featureName}`).join("<br/>") || "";
    }

    // ----------------------
    // Section: Modal detail lifecycle (show/hide/load)
    // ----------------------

    /**
     * @description Show detail modal: set customerDetail và load data detail
     * @param data - customer object
     */
    async showDetail(data) {
        this.featureString = "";
        this.customerDetail = data;
        this.loadDataDetail(this.customerDetail);
    }

    /**
     * @description Hide modal detail
     */
    hideModal() {
        this.$bvModal.hide('bv-modal-detail');
    }

    /**
     * @description Load mockup / real data cho detail modal:
     *  - fetchOverview
     *  - fetchCustomer
     *  - fetchMeetingParticipantsCountData
     *  - update subPlansOverView từ subscriptionPlanService.getPlans()
     * @param data - customer object
     */
    async loadDataDetail(data) {
        await this.fetchOverview(data);
        await this.fetchCustomer(data.id);
        await this.fetchMeetingParticipantsCountData(data);
        let plans = await subscriptionPlanService.getPlans();
        this.subPlansOverView = plans;
        // Chuẩn bị tool-tip danh sách tính tăng của gói cước
    }

    // ----------------------
    // Section: Watch subPlansOverView để update featureString nếu cần
    // ----------------------

    /**
     * @watch subPlansOverView
     * @description Khi subPlansOverView thay đổi, nếu customerDetail có subPlanId -> cập nhật featureString
     */
    @Watch('subPlansOverView', { deep: true })
    onSubPlansOverViewLoaded() {
        if (this.customerDetail?.subPlanId) {
            const dataFeature = this.subPlansOverView.find(p => p.id == this.customerDetail.subPlanId);
            if (dataFeature) {
                this.featureString = dataFeature.features
                    ?.map(f => `&#10003; ${f.featureName}`)
                    .join('<br/>') || "";
            }
        }
    }

    /**
     * @description Convert date string từ overview (YYYY-MM-DDTHH:mm:ss) sang format hiển thị (convertDate)
     * @param data - chuỗi ngày (string)
     * @returns string - kết quả convertDate(data)
     */
    convertDateOverView(data) {
        data = moment(data, 'YYYY-MM-DDTHH:mm:ss');
        return convertDate(data);
    }

    // #endregion METHODS
}
</script>
